// Сервис для работы со сменами Beauty-автопилот

import { Shift, CreateShiftRequest, UpdateShiftRequest, ShiftFilters, AutoFillRequest, AutoFillResponse } from '../types/shift.types';
import { getInstance } from '../../../auth/authWrapper';
import cfg from '../../../../config';

class ShiftService {
  private baseUrl = `${cfg.apiEndpoint}/beautyAutopilot/shifts`;

  // Получить заголовки для API запросов
  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    try {
      const auth = getInstance();
      const token = await auth.getTokenSilently();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      // Ошибка получения токена авторизации
    }
    
    return headers;
  }

  // Получить список смен
  async getShifts(filters?: ShiftFilters, projectId?: string): Promise<Shift[]> {
    try {
      const queryParams = new URLSearchParams();
      
      // Обязательный параметр projectId
      if (projectId) {
        queryParams.append('projectId', projectId);
      }
      
      if (filters?.planId) queryParams.append('planId', filters.planId);
      
      // Передаем месяц и год вместо dateFrom/dateTo
      if (filters && 'month' in filters && filters.month) queryParams.append('month', filters.month.toString());
      if (filters && 'year' in filters && filters.year) queryParams.append('year', filters.year.toString());
      
      if (filters?.branch) queryParams.append('branch', filters.branch);
      if (filters?.channel) queryParams.append('channel', filters.channel.toString());
      if (filters?.administratorId) queryParams.append('administratorId', filters.administratorId);
      if (filters?.status) queryParams.append('status', filters.status);

      const url = `${this.baseUrl}?${queryParams.toString()}`;
      // Запрос к API

      const response = await fetch(url, {
        headers: await this.getHeaders()
      });
      
      // Статус ответа API
      if (!response.ok) {
        const errorText = await response.text();
        // Ошибка ответа API
        throw new Error(`Failed to fetch shifts: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      // Данные ответа API
      return data;
    } catch (error) {
      // Ошибка получения смен
      throw error;
    }
  }

  // Получить смену по ID
  async getShift(id: string): Promise<Shift> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        headers: await this.getHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch shift');
      
      return await response.json();
    } catch (error) {
      // Ошибка получения смены
      throw error;
    }
  }

  // Создать новую смену
  async createShift(shiftData: CreateShiftRequest): Promise<Shift> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: await this.getHeaders(),
        body: JSON.stringify(shiftData),
      });
      
      if (!response.ok) {
        // Если это ошибка дублирования (409 Conflict)
        if (response.status === 409) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Смена с указанными данными уже существует');
        }
        throw new Error('Failed to create shift');
      }
      
      return await response.json();
    } catch (error) {
      // Ошибка создания смены
      throw error;
    }
  }

  // Обновить смену
  async updateShift(shiftData: UpdateShiftRequest): Promise<Shift> {
    try {
      // Обновление смены
      
      const response = await fetch(`${this.baseUrl}/${shiftData.id}`, {
        method: 'PUT',
        headers: await this.getHeaders(),
        body: JSON.stringify(shiftData),
      });
      
      // Статус ответа обновления смены
      
      if (!response.ok) {
        const errorText = await response.text();
        // Ошибка ответа обновления смены
        throw new Error(`Failed to update shift: ${response.status} ${errorText}`);
      }
      
      const result = await response.json();
      // Успешный результат обновления смены
      return result;
    } catch (error) {
      // Ошибка обновления смены
      throw error;
    }
  }

  // Автозаполнение показателей
  async autoFillIndicators(request: AutoFillRequest): Promise<AutoFillResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/${request.shiftId}/auto-fill`, {
        method: 'POST',
        headers: await this.getHeaders(),
        body: JSON.stringify(request),
      });
      
      if (!response.ok) throw new Error('Failed to auto-fill indicators');
      
      return await response.json();
    } catch (error) {
      // Ошибка автозаполнения индикаторов
      throw error;
    }
  }

  // Удалить смену
  async deleteShift(id: string, projectId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}?projectId=${projectId}`, {
        method: 'DELETE',
        headers: await this.getHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to delete shift');
    } catch (error) {
      // Ошибка удаления смены
      throw error;
    }
  }

  // Получить смены по плану
  async getShiftsByPlan(planId: string, filters?: Omit<ShiftFilters, 'planId'>): Promise<Shift[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('planId', planId);
      
      if (filters && 'month' in filters && filters.month) queryParams.append('month', filters.month.toString());
      if (filters && 'year' in filters && filters.year) queryParams.append('year', filters.year.toString());
      if (filters?.branch) queryParams.append('branch', filters.branch);
      if (filters?.channel) queryParams.append('channel', filters.channel.toString());
      if (filters?.administratorId) queryParams.append('administratorId', filters.administratorId);
      if (filters?.status) queryParams.append('status', filters.status);

      const response = await fetch(`${this.baseUrl}/by-plan?${queryParams.toString()}`, {
        headers: await this.getHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch shifts by plan');
      
      return await response.json();
    } catch (error) {
      // Ошибка получения смен по плану
      throw error;
    }
  }

  // Получить смены по администратору
  async getShiftsByAdministrator(administratorId: string, filters?: Omit<ShiftFilters, 'administratorId'>): Promise<Shift[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('administratorId', administratorId);
      
      if (filters?.planId) queryParams.append('planId', filters.planId);
      if (filters && 'month' in filters && filters.month) queryParams.append('month', filters.month.toString());
      if (filters && 'year' in filters && filters.year) queryParams.append('year', filters.year.toString());
      if (filters?.branch) queryParams.append('branch', filters.branch);
      if (filters?.channel) queryParams.append('channel', filters.channel.toString());
      if (filters?.status) queryParams.append('status', filters.status);

      const response = await fetch(`${this.baseUrl}/by-administrator?${queryParams.toString()}`, {
        headers: await this.getHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch shifts by administrator');
      
      return await response.json();
    } catch (error) {
      // Ошибка получения смен по администратору
      throw error;
    }
  }

  // Получить статистику по сменам
  async getShiftStatistics(filters?: ShiftFilters): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.planId) queryParams.append('planId', filters.planId);
      if (filters && 'month' in filters && filters.month) queryParams.append('month', filters.month.toString());
      if (filters && 'year' in filters && filters.year) queryParams.append('year', filters.year.toString());
      if (filters?.branch) queryParams.append('branch', filters.branch);
      if (filters?.channel) queryParams.append('channel', filters.channel.toString());
      if (filters?.administratorId) queryParams.append('administratorId', filters.administratorId);
      if (filters?.status) queryParams.append('status', filters.status);

      const response = await fetch(`${this.baseUrl}/statistics?${queryParams.toString()}`, {
        headers: await this.getHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch shift statistics');
      
      return await response.json();
    } catch (error) {
      // Ошибка получения статистики смен
      throw error;
    }
  }

}

export const shiftService = new ShiftService();
export default shiftService;
